import React from "react";
import {
  Upload as KendoUpload,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
  UploadFileInfo,
} from "@progress/kendo-react-upload";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Label, Error, Hint } from "@progress/kendo-react-labels";
import { TbCircleXFilled } from "react-icons/tb";
import { PiUploadSimpleBold } from "react-icons/pi";
import { FaFile } from "react-icons/fa6";
import { CgDanger } from "react-icons/cg";

interface CustomFileInfo extends UploadFileInfo {
  getRawFile?: () => File;
}

const FormImageUploadMorden = (fieldRenderProps: FieldRenderProps) => {
  const {
    valid,
    value,
    id,
    optional,
    label,
    hint,
    astrike = false,
    validationMessage,
    touched,
    acceptFileTypesMessage,
    ...others
  } = fieldRenderProps;

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const hasFile =
    (value && value.length > 0) || (typeof value === "string" && value);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  React.useEffect(() => {
    // Handle already uploaded image (URL string)
    if (typeof value === "string" && value) {
      setPreviewUrl(value);
    }
    // Handle new file uploads
    else if (Array.isArray(value) && value.length > 0) {
      const fileInfo = value[0] as CustomFileInfo;
      if (fileInfo.getRawFile) {
        const file = fileInfo.getRawFile();
        if (file instanceof File) {
          const reader = new FileReader();
          reader.onload = () => setPreviewUrl(reader.result as string);
          reader.readAsDataURL(file);
        }
      } else if (fileInfo.uid) {
        // Handle case where file is already uploaded (has uid but no getRawFile)
        setPreviewUrl(fileInfo.name); // Use name as URL if needed
      }
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const onChangeHandler = (event: UploadOnAddEvent) => {
    fieldRenderProps.onChange({ value: event.newState });
  };

  const onRemoveHandler = (event: UploadOnRemoveEvent) => {
    fieldRenderProps.onChange({ value: event.newState });
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fieldRenderProps.onChange({ value: [] });
    setPreviewUrl(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      const fakeEvent = {
        newState: [
          {
            getRawFile: () => file,
            name: file.name,
            size: file.size,
            uid: String(Math.random()),
          },
        ],
      } as UploadOnAddEvent;
      onChangeHandler(fakeEvent);
    }
  };

  return (
    <FieldWrapper style={{ width: "100%" }}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        optional={optional}
        style={{ marginBottom: 8 }}
      >
        {label} {astrike && <span style={{ color: "red" }}>*</span>}
      </Label>

      <div
        style={{
          width: "auto",
          height: 150,
          border: `2px dashed ${hasFile ? "#4CAF50" : "#9E9E9E"}`,
          borderRadius: 8,
          padding: 0,
          backgroundColor: "#FAFAFA",
          transition: "all 0.3s ease",
          textAlign: "center",
          cursor: "pointer",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
        }}
        onClick={handleBrowseClick}
      >
        {hasFile && previewUrl ? (
          <>
            <TbCircleXFilled
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                fontSize: 28,
                color: "red",
                zIndex: 1,
              }}
              onClick={handleRemoveClick}
            />
            {["pdf", "doc", "docx"].includes(
              previewUrl.split(".").pop()?.toLowerCase() || ""
            ) ? (
              <>
                <FaFile style={{ fontSize: "32px", color: "green" }} />
                <b style={{ marginTop: 5 }}>File Type</b>
              </>
            ) : (
              <>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </>
            )}
          </>
        ) : (
          <>
            <div
              style={{
                width: 48,
                height: 48,
                backgroundColor: "#E3F2FD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <PiUploadSimpleBold
                style={{ fontSize: "28px", color: "#2196F3" }}
              />
            </div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Select File</div>
            <div style={{ color: "#757575", fontSize: 14 }}>
              or{" "}
              <span style={{ color: "#2196F3", textDecoration: "underline" }}>
                browse files
              </span>
              <br />
              <p>{acceptFileTypesMessage}</p>
            </div>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept={others.accept}
      />

      {/* KendoUpload for drag and drop functionality */}
      <KendoUpload
        id={id}
        autoUpload={false}
        showActionButtons={false}
        multiple={false}
        files={Array.isArray(value) ? value : []}
        onAdd={onChangeHandler}
        onRemove={onRemoveHandler}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ariaLabelledBy={labelId}
        // style={{ display: "none" }}
        className="d-none"
        {...others}
      />

      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && (
        <Error className="d-flex justify-content-end" id={errorId}>
          <span
            className="d-flex align-items-center"
            style={{
              background: "#DF4957",
              color: "white",
              padding: "1px 8px 1px 5px",
              borderRadius: 10,
              gap: 5,
            }}
          >
            <CgDanger style={{ marginBottom: 2 }} /> {validationMessage}
          </span>
        </Error>
      )}
    </FieldWrapper>
  );
};

export default FormImageUploadMorden;
